// Client helper for Google reCAPTCHA v3 (invisible, score-based).
//
// The site key is public and injected at build time. When it is absent (e.g.
// local dev without keys) every helper no-ops so the signup flow keeps working
// — the server side mirrors this fail-open behaviour.

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = 'recaptcha-v3';

export const isRecaptchaConfigured = () => !!SITE_KEY;

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      reject(new Error('recaptcha-no-window'));
      return;
    }
    if (window.grecaptcha?.execute) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('recaptcha-load-failed')));
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('recaptcha-load-failed'));
    document.head.appendChild(s);
  });

// Eagerly load the reCAPTCHA script (without executing) when the signup form is
// shown, so by submit time the token is instant AND — more importantly — v3 has
// observed the user's on-page behaviour, which yields a more reliable score than
// loading it only at submit. Safe to call repeatedly; no-op without a site key.
export const preloadRecaptcha = () => {
  if (!SITE_KEY) {
    return;
  }
  // Best-effort: a failure here is retried (and surfaced) at submit time by
  // executeRecaptcha, so swallow it.
  loadScript().catch(() => {});
};

// Returns a fresh reCAPTCHA token for the given action, or `null` when
// reCAPTCHA is not configured or the script could not run. Callers treat a
// `null` here together with the server's fail-open response.
export const executeRecaptcha = async action => {
  if (!SITE_KEY) {
    return null;
  }
  try {
    await loadScript();
    await new Promise(resolve => window.grecaptcha.ready(resolve));
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch (e) {
    // Surface why no token was produced (e.g. "Invalid domain for site key",
    // blocked script) — otherwise the server just sees a missing token.
    console.warn('reCAPTCHA could not produce a token:', e?.message || e);
    return null;
  }
};
